import { useEffect, useState } from "react";
import type { Appointment, AppointmentSlots } from "@dentacore/shared-types";

import { listSlots, moveAppointment } from "../api/appointments";
import { clockAt } from "./grid-geometry";
import { Modal } from "../ui/modal";
import styles from "./reschedule-picker.module.css";
import { useBookingScoped } from "./booking-location";

/**
 * The non-drag way to move a booking (STORY-035).
 *
 * ── Nothing here decides what is legal ──────────────────────────────────────
 *
 * The list comes from `GET /appointments/:id/slots`, which the conflict service
 * generates from the same occupancy arithmetic every other write consults.
 * **This component does not filter it, sort it or add to it** — a browser-side
 * rule here would be a second implementation of turnover, and the story's
 * acceptance check is precisely that an offered slot is never refused.
 *
 * ── The offer can still go stale, and that is handled rather than prevented ──
 *
 * Between reading the list and choosing from it, a colleague can book the slot.
 * Choosing calls `PATCH :id/move`, which re-runs the check (§6.1 — *re-validates
 * at confirm time*) and can refuse; a refusal shows the server's own sentence
 * and **re-reads the list**, so the taken time disappears rather than sitting
 * there inviting a second attempt.
 *
 * ── Any day, any chair — since STORY-116 ────────────────────────────────────
 *
 * **This used to be same-day, same-chair**, described as *drag without a
 * mouse* (P3 Q12, 2026-08-23), and moving to another day was not offered here
 * or anywhere else. That left the product with **no way to move a booking onto
 * another day at all** — recorded as a known gap for a fortnight, and made
 * unavoidable by multi-part treatment plans, where a three-visit crown needs
 * its parts moved between weeks.
 *
 * **The server could already do it.** `PATCH :id/move` had always accepted an
 * arbitrary day and chair, and the conflict service never cared which day it
 * was asked about. The restriction was here: the picker only ever asked for
 * the booking's own day, so there was nothing to choose. The day and chair
 * controls are the whole fix.
 *
 * **The mockup draws this inside the selected-appointment panel** (`startResched`
 * / `sel.slots`), which is STORY-036 and does not exist yet. The wording and the
 * information are the mockup's; the dialog shell is the one the patient modals
 * already use, so a fourth modal shape is not invented. When 036 lands, the
 * button moves into the panel and opens this same component.
 */
export function ReschedulePicker({
  appointment,
  chairName,
  chairs,
  onMove,
  onMoved,
  onClose,
}: {
  appointment: Appointment;
  chairName: string;
  /** This branch's chairs, so the picker can offer another room (STORY-116). */
  chairs: readonly { id: string; name: string }[];
  /**
   * The page's move, which asks about the rest of a treatment plan first
   * (CH4). Absent, the picker moves the booking itself. `"cancelled"` means the
   * person closed that question, and nothing moved.
   */
  onMove?: (startsAt: string, chairId?: string) => Promise<"moved" | "cancelled">;
  onMoved: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const here = useBookingScoped();
  const [slots, setSlots] = useState<AppointmentSlots | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * `reload` counts rather than toggles, so a second refusal re-runs the effect
   * even when the first already set it — a boolean would settle on `true` and
   * stop refreshing after one stale pick.
   */
  const [reload, setReload] = useState(0);

  /**
   * Which day and room the picker is looking at — STORY-116.
   *
   * **Starts on the booking's own day**, so opening the picker shows exactly
   * what it always showed. Changing either re-asks the server; nothing is
   * filtered in the browser, because every slot offered has to be one a move
   * made straight away would accept, and only the conflict service knows that.
   */
  const [onDate, setOnDate] = useState(() => appointment.startsAt.slice(0, 10));
  const [chairId, setChairId] = useState(appointment.chairId);

  useEffect(() => {
    const controller = new AbortController();

    here(listSlots)(appointment.id, onDate, chairId, controller.signal)
      .then(setSlots)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        // An empty list and a failed read look identical once rendered, and the
        // second is a fault worth seeing rather than a full chair.
        setSlots({ localDate: "", timezone: "UTC", durationMinutes: 0, startsAt: [] });
        setError(cause instanceof Error ? cause.message : "Could not read the free times.");
      });

    return () => controller.abort();
  }, [appointment.id, reload, onDate, chairId]);

  /** The room being looked at, which since 116 need not be the booking's own. */
  const lookingAtChair = chairs.find((chair) => chair.id === chairId)?.name ?? chairName;

  const pick = (startsAt: string): void => {
    setBusy(true);
    setError(null);

    // The chair goes with it: picking a slot in another room is a cross-chair
    // move, and the endpoint has always taken both together.
    const newChair = chairId === appointment.chairId ? undefined : chairId;
    const moving =
      onMove !== undefined
        ? onMove(startsAt, newChair)
        : here(moveAppointment)(appointment.id, {
            startsAt,
            ...(newChair === undefined ? {} : { chairId: newChair }),
          }).then(() => "moved" as const);

    moving
      .then((result) => {
        if (result === "moved") onMoved();
      })
      .catch((cause: unknown) => {
        // The server's own sentence. The mockup says "That slot is taken — pick
        // another."; the server says which booking got in the way and when the
        // branch is open, which is strictly more useful and already written.
        setError(cause instanceof Error ? cause.message : "That slot is no longer free.");
        setReload((count) => count + 1);
      })
      .finally(() => setBusy(false));
  };

  return (
    <Modal label="Move to a new time" testId="reschedule-picker" onClose={onClose}>
      <h2 className={styles.title}>Move to a new time</h2>

      {/*
          The mockup's own note, with the chair and the length it needs:
          "30 min needed · only free times in Chair 1 are listed".

          **The chair it names is now the one being looked at**, not the one the
          booking is in — since STORY-116 those can differ, and naming the
          booking's chair while listing another room's slots would be worse
          than saying nothing.
        */}
      <p className={styles.note} data-testid="reschedule-note">
        {slots === null
          ? "Reading the free times…"
          : `${String(slots.durationMinutes)} min needed · only free times in ${lookingAtChair} are listed`}
      </p>

      {/*
          **Another day, another room** (STORY-116). The gap this closed had
          been open since Phase 3: the picker could only ever reach the day the
          booking was already on, so a three-visit crown could not have its
          parts moved between weeks — which is why a treatment-plan phase needed
          a calendar story first.
        */}
      <div className={styles.controls} data-testid="reschedule-controls">
        <label className={styles.control}>
          <span className={styles.controlLabel}>Day</span>
          <input
            type="date"
            className={styles.controlInput}
            value={onDate}
            onChange={(event) => setOnDate(event.target.value)}
            data-testid="reschedule-date"
          />
        </label>

        {chairs.length > 1 && (
          <label className={styles.control}>
            <span className={styles.controlLabel}>Chair</span>
            <select
              className={styles.controlInput}
              value={chairId}
              onChange={(event) => setChairId(event.target.value)}
              data-testid="reschedule-chair"
            >
              {chairs.map((chair) => (
                <option key={chair.id} value={chair.id}>
                  {chair.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {error !== null && (
        <p className={styles.error} data-testid="reschedule-error">
          {error}
        </p>
      )}

      {slots !== null && slots.startsAt.length === 0 && error === null && (
        <p className={styles.empty} data-testid="reschedule-empty">
          Nothing fits in {lookingAtChair} that day. Try another day or another chair above.
        </p>
      )}

      {slots !== null && slots.startsAt.length > 0 && (
        <div className={styles.slots} data-testid="reschedule-slots">
          {slots.startsAt.map((instant) => {
            const current = instant === appointment.startsAt;

            return (
              <button
                key={instant}
                type="button"
                className={current ? styles.current : styles.slot}
                disabled={busy}
                onClick={() => pick(instant)}
                data-testid="reschedule-slot"
                data-starts-at={instant}
                // Its own time is legal — the conflict check excludes the
                // booking from its own occupancy — so it appears in the list
                // and is said out loud rather than silently doing nothing.
                title={current ? "Where it is now" : undefined}
              >
                {clockAt(instant, slots.timezone)}
                {current && <span className={styles.currentTag}>now</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.secondary}
          onClick={onClose}
          data-testid="reschedule-close"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
}
